import * as THREE from 'three';
import { createSeededRandom } from '../utils/random';
import { ATTACKS, type AttackStyle } from '../game/combat';
import { WEAPONS, type WeaponType } from '../game/weapons';
import { DEFAULT_IMPACT_WORDS, normalizeImpactWords, type ImpactWords } from '../systems/ImpactWords';

const POOL_SIZE = 1024;
const MAX_RINGS = 8;

interface BurstOptions {
  speed?: number;
  spread?: number;
  life?: number;
  gravity?: number;
  drag?: number;
  upBias?: number;
}

interface RingState {
  mesh: THREE.Mesh;
  material: THREE.MeshBasicMaterial;
  life: number;
  ttl: number;
  maxRadius: number;
}

function softDotTexture(): THREE.CanvasTexture {
  const canvas = document.createElement('canvas');
  canvas.width = 64;
  canvas.height = 64;
  const context = canvas.getContext('2d');
  if (!context) throw new Error('no canvas');
  const gradient = context.createRadialGradient(32, 32, 2, 32, 32, 30);
  gradient.addColorStop(0, 'rgba(255,255,255,1)');
  gradient.addColorStop(0.4, 'rgba(255,255,255,0.55)');
  gradient.addColorStop(1, 'rgba(255,255,255,0)');
  context.fillStyle = gradient;
  context.fillRect(0, 0, 64, 64);
  const texture = new THREE.CanvasTexture(canvas);
  return texture;
}

// A tapered comic crescent reads as motion, using fewer faces than a thin tube.
function attackRibbon(): THREE.BufferGeometry {
  const positions: number[] = [], colors: number[] = [], indices: number[] = [];
  const segments = 32;
  for (let i = 0; i <= segments; i++) {
    const t = i / segments;
    const angle = t * Math.PI * 1.45;
    const width = Math.sin(Math.PI * t) * (0.08 + t * 0.2);
    for (let lane = 0; lane < 3; lane++) {
      const radius = 1 + (lane - 1) * width;
      positions.push(Math.cos(angle) * radius, Math.sin(angle) * radius, 0);
      const value = lane === 1 ? 1 : .3;
      colors.push(value, value, value);
    }
    if (i < segments) for (let lane = 0; lane < 2; lane++) {
      const a = i * 3 + lane;
      indices.push(a, a + 3, a + 1, a + 1, a + 3, a + 4);
    }
  }
  const geometry = new THREE.BufferGeometry();
  geometry.setAttribute('position', new THREE.Float32BufferAttribute(positions, 3));
  geometry.setAttribute('color', new THREE.Float32BufferAttribute(colors, 3));
  geometry.setIndex(indices);
  geometry.computeBoundingSphere();
  return geometry;
}

export class VfxSystem {
  private readonly rng = createSeededRandom(123);
  private readonly positions = new Float32Array(POOL_SIZE * 3);
  private readonly colors = new Float32Array(POOL_SIZE * 3);
  private readonly renderPositions = new Float32Array(POOL_SIZE * 3);
  private readonly renderColors = new Float32Array(POOL_SIZE * 3);
  private readonly baseColors = new Float32Array(POOL_SIZE * 3);
  private readonly velocities = new Float32Array(POOL_SIZE * 3);
  private readonly life = new Float32Array(POOL_SIZE);
  private readonly ttl = new Float32Array(POOL_SIZE);
  private readonly grav = new Float32Array(POOL_SIZE);
  private readonly drag = new Float32Array(POOL_SIZE);
  private readonly activeParticles = new Set<number>();
  private cursor = 0;

  private readonly ringPool: RingState[] = [];
  private ringCursor = 0;
  private readonly fireballs: Array<{ mesh: THREE.InstancedMesh<THREE.LatheGeometry, THREE.MeshBasicMaterial>; life: number }> = [];
  private fireballCursor = 0;
  private readonly hitLabels: Array<{ sprite: THREE.Sprite; life: number }> = [];
  private readonly hitTexture: THREE.CanvasTexture;
  private readonly attackTextures = new Map<AttackStyle, THREE.CanvasTexture>();
  private readonly weaponTextures = new Map<WeaponType, THREE.CanvasTexture>();
  private readonly fragments: Array<{
    mesh: THREE.Mesh<THREE.TetrahedronGeometry, THREE.MeshBasicMaterial>;
    velocity: THREE.Vector3; life: number;
  }> = [];
  private fragmentCursor = 0;
  private readonly arcs: Array<{ mesh: THREE.Mesh<THREE.BufferGeometry, THREE.MeshBasicMaterial>; life: number; style: AttackStyle; yaw: number; height: number }> = [];
  private arcCursor = 0;
  private hitCursor = 0;

  private readonly chargeRing: THREE.Mesh;
  private readonly chargeMaterial: THREE.MeshBasicMaterial;

  readonly group = new THREE.Group();

  constructor() {
    const cloudGeometry = new THREE.LatheGeometry([[0, -0.4], [0.4, -0.28], [0.56, 0], [0.4, 0.3], [0.15, 0.6], [0.2, 0.78], [0, 1.2]].map(([x, y]) => new THREE.Vector2(x, y)), 10);
    const flamePositions = cloudGeometry.getAttribute('position');
    const flameColors = new Float32Array(flamePositions.count * 3);
    for (let i = 0; i < flamePositions.count; i++) {
      const height = flamePositions.getY(i);
      flamePositions.setX(i, flamePositions.getX(i) + Math.max(0, height) ** 2 * 0.15);
      const color = new THREE.Color().setHSL(0.05 + 0.09 * Math.max(0, 1 - height), 1, 0.45 + 0.35 * Math.max(0, 0.6 - height));
      flameColors.set([color.r, color.g, color.b], i * 3);
    }
    cloudGeometry.setAttribute('color', new THREE.BufferAttribute(flameColors, 3));
    const transform = new THREE.Matrix4();
    const offsets = [[0, 0, 0], [-0.55, 0.15, 0.1], [0.55, 0.2, -0.1], [0.1, 0.75, 0], [0, 0.2, 0.55]];
    for (let i = 0; i < 6; i++) {
      const mesh = new THREE.InstancedMesh(cloudGeometry, new THREE.MeshBasicMaterial({ vertexColors: true, transparent: true, depthWrite: false, blending: THREE.AdditiveBlending }), offsets.length);
      offsets.forEach(([x, y, z], index) => {
        mesh.setMatrixAt(index, transform.makeTranslation(x, y, z));
        mesh.setColorAt(index, new THREE.Color(index === 0 ? '#fff2b1' : index % 2 ? '#ffaf25' : '#ff6727'));
      });
      mesh.name = 'bazooka-fireball';
      mesh.visible = false;
      mesh.frustumCulled = false;
      this.fireballs.push({ mesh, life: 0 });
      this.group.add(mesh);
    }
    this.hitTexture = this.createLabelTexture();
    for (const style of Object.keys(ATTACKS) as AttackStyle[]) this.attackTextures.set(style, this.createLabelTexture());
    for (const kind of Object.keys(WEAPONS) as WeaponType[]) this.weaponTextures.set(kind, this.createLabelTexture());
    this.setImpactWords(DEFAULT_IMPACT_WORDS);
    const arcGeometry = attackRibbon();
    const fragmentGeometry = new THREE.TetrahedronGeometry(0.18);
    for (let i = 0; i < 24; i += 1) {
      const mesh = new THREE.Mesh(fragmentGeometry, new THREE.MeshBasicMaterial({ color: '#ffffff' }));
      mesh.name = 'weapon-fragment';
      mesh.visible = false;
      this.fragments.push({ mesh, velocity: new THREE.Vector3(), life: 0 });
      this.group.add(mesh);
    }
    for (let i = 0; i < MAX_RINGS; i += 1) {
      const mesh = new THREE.Mesh(arcGeometry, new THREE.MeshBasicMaterial({
        color: '#ffd23f', vertexColors: true, transparent: true, depthWrite: false, side: THREE.DoubleSide,
      }));
      mesh.name = 'attack-motion-ribbon';
      mesh.visible = false;
      mesh.rotation.order = 'YXZ';
      this.arcs.push({ mesh, life: 0, style: 'punch', yaw: 0, height: 0 });
      this.group.add(mesh);
    }
    for (let i = 0; i < MAX_RINGS; i += 1) {
      const sprite = new THREE.Sprite(new THREE.SpriteMaterial({
        map: this.hitTexture, depthTest: false, depthWrite: false, transparent: true,
      }));
      sprite.visible = false;
      sprite.renderOrder = 6;
      this.hitLabels.push({ sprite, life: 0 });
      this.group.add(sprite);
    }
    for (let i = 0; i < POOL_SIZE; i += 1) {
      this.positions[i * 3 + 1] = -999;
    }
    const geometry = new THREE.BufferGeometry();
    geometry.setAttribute('position', new THREE.BufferAttribute(this.renderPositions, 3).setUsage(THREE.DynamicDrawUsage));
    geometry.setAttribute('color', new THREE.BufferAttribute(this.renderColors, 3).setUsage(THREE.DynamicDrawUsage));
    geometry.setDrawRange(0, 0);
    const material = new THREE.PointsMaterial({
      size: 0.26,
      map: softDotTexture(),
      vertexColors: true,
      blending: THREE.AdditiveBlending,
      transparent: true,
      depthWrite: false,
      sizeAttenuation: true,
    });
    const points = new THREE.Points(geometry, material);
    points.frustumCulled = false;
    points.renderOrder = 5;
    this.group.add(points);
    this.points = points;

    const ringGeometry = new THREE.RingGeometry(0.8, 1.0, 36);
    const ringPositions = ringGeometry.getAttribute('position');
    for (let i = 0; i < ringPositions.count; i++) {
      const tooth = i % 37 % 2 ? 0.88 : 1;
      ringPositions.setXY(i, ringPositions.getX(i) * tooth, ringPositions.getY(i) * tooth);
    }
    ringGeometry.computeBoundingSphere();
    for (let i = 0; i < MAX_RINGS; i += 1) {
      const ringMaterial = new THREE.MeshBasicMaterial({
        color: '#ffffff',
        transparent: true,
        opacity: 0,
        blending: THREE.AdditiveBlending,
        depthWrite: false,
        side: THREE.DoubleSide,
      });
      const ring = new THREE.Mesh(ringGeometry, ringMaterial);
      ring.rotation.x = -Math.PI / 2;
      ring.visible = false;
      ring.renderOrder = 4;
      this.ringPool.push({ mesh: ring, material: ringMaterial, life: 0, ttl: 1, maxRadius: 1 });
      this.group.add(ring);
    }

    this.chargeMaterial = new THREE.MeshBasicMaterial({
      color: '#ffffff',
      transparent: true,
      opacity: 0,
      blending: THREE.AdditiveBlending,
      depthWrite: false,
      side: THREE.DoubleSide,
    });
    this.chargeRing = new THREE.Mesh(new THREE.RingGeometry(0.55, 0.72, 40), this.chargeMaterial);
    this.chargeRing.rotation.x = -Math.PI / 2;
    this.chargeRing.visible = false;
    this.chargeRing.renderOrder = 4;
    this.group.add(this.chargeRing);
  }

  private words: ImpactWords = { ...DEFAULT_IMPACT_WORDS };

  get impactWords(): Readonly<ImpactWords> { return this.words; }

  private createLabelTexture(): THREE.CanvasTexture {
    const canvas = document.createElement('canvas');
    canvas.width = 512;
    canvas.height = 128;
    const texture = new THREE.CanvasTexture(canvas);
    texture.colorSpace = THREE.SRGBColorSpace;
    return texture;
  }

  setImpactWords(words: ImpactWords): void {
    this.words = normalizeImpactWords(words);
    this.drawLabel(this.hitTexture, this.words.generic, '#ffd23f');
    for (const [style, texture] of this.attackTextures) this.drawLabel(texture, this.words[style], ATTACKS[style].color);
    for (const [kind, texture] of this.weaponTextures) this.drawLabel(texture, this.words[kind], WEAPONS[kind].color);
  }

  private drawLabel(texture: THREE.CanvasTexture, word: string, color: string): void {
    const canvas = texture.image as HTMLCanvasElement;
    const context = canvas.getContext('2d');
    if (!context) throw new Error('Cannot create hit label');
    context.clearRect(0, 0, canvas.width, canvas.height);
    context.beginPath();
    for (let i = 0; i < 24; i += 1) {
      const angle = i * Math.PI / 12;
      const radius = i % 2 === 0 ? 1 : 0.72;
      context.lineTo(252 + Math.cos(angle) * 240 * radius, 61 + Math.sin(angle) * 53 * radius);
    }
    context.closePath();
    context.save();
    context.translate(5, 6);
    context.fillStyle = '#172337';
    context.fill();
    context.restore();
    context.fillStyle = color;
    context.fill();
    context.strokeStyle = '#252030';
    context.lineWidth = 7;
    context.stroke();
    context.save();
    context.clip();
    context.fillStyle = '#17233722';
    for (let y = 10; y < 120; y += 12) {
      for (let x = 12; x < 500; x += 14) {
        context.beginPath();
        context.arc(x + (y % 24 ? 7 : 0), y, 2, 0, Math.PI * 2);
        context.fill();
      }
    }
    context.restore();
    context.fillStyle = '#252030';
    context.font = '900 46px "Arial Black", sans-serif';
    const width = context.measureText(word).width;
    if (width > 340) context.font = `900 ${46 * 340 / width}px "Arial Black", sans-serif`;
    context.textAlign = 'center';
    context.textBaseline = 'middle';
    context.strokeStyle = '#fff6d1';
    context.lineWidth = 4;
    context.strokeText(word, 252, 62, 340);
    context.fillText(word, 252, 62, 340);
    texture.needsUpdate = true;
  }

  private readonly points: THREE.Points;

  get activeHitLabels(): number {
    return this.hitLabels.filter(label => label.life > 0).length;
  }

  private spawn(origin: THREE.Vector3, color: THREE.Color, options: BurstOptions): void {
    const index = this.cursor;
    this.cursor = (this.cursor + 1) % POOL_SIZE;
    this.activeParticles.add(index);
    this.positions[index * 3] = origin.x;
    this.positions[index * 3 + 1] = origin.y;
    this.positions[index * 3 + 2] = origin.z;
    const speed = options.speed ?? 4;
    const spread = options.spread ?? 1;
    const theta = this.rng() * Math.PI * 2;
    const phi = Math.acos(2 * this.rng() - 1);
    const dirX = Math.sin(phi) * Math.cos(theta);
    const dirY = Math.cos(phi) * (options.upBias ?? 0.5) + 0.6 * (options.upBias ?? 0.5);
    const dirZ = Math.sin(phi) * Math.sin(theta);
    this.velocities[index * 3] = dirX * speed * spread;
    this.velocities[index * 3 + 1] = Math.abs(dirY) * speed * 0.8 + speed * 0.2;
    this.velocities[index * 3 + 2] = dirZ * speed * spread;
    this.life[index] = 1;
    this.ttl[index] = options.life ?? 0.7;
    this.life[index] = this.ttl[index];
    this.grav[index] = options.gravity ?? 9;
    this.drag[index] = options.drag ?? 1.6;
    this.baseColors[index * 3] = color.r;
    this.baseColors[index * 3 + 1] = color.g;
    this.baseColors[index * 3 + 2] = color.b;
    this.colors[index * 3] = color.r;
    this.colors[index * 3 + 1] = color.g;
    this.colors[index * 3 + 2] = color.b;
  }

  spawnBurst(origin: THREE.Vector3, color: string | number, count: number, options: BurstOptions = {}): void {
    const c = new THREE.Color(color);
    for (let i = 0; i < count; i += 1) this.spawn(origin, c, options);
  }

  spawnDust(origin: THREE.Vector3, count = 8): void {
    const c = new THREE.Color('#c8c2b4');
    for (let i = 0; i < count; i += 1) {
      this.spawn(origin, c, { speed: 1.6, spread: 1.4, life: 0.7, gravity: 2.2, drag: 2.6, upBias: 0.7 });
    }
  }

  spawnConfetti(origin: THREE.Vector3, count = 90): void {
    const palette = ['#ff5d5d', '#57c7ff', '#ffd23f', '#8dffab', '#ff9de2', '#ffffff'];
    for (let i = 0; i < count; i += 1) {
      const c = new THREE.Color(palette[Math.floor(this.rng() * palette.length)]);
      this.spawn(origin, c, { speed: 7, spread: 1.6, life: 1.7, gravity: 5.5, drag: 0.7, upBias: 1 });
    }
  }

  spawnExplosion(origin: THREE.Vector3): void {
    this.spawnBurst(origin, '#ffb347', 34, { speed: 9, spread: 1.2, life: 0.55, gravity: 4, drag: 2.4 });
    this.spawnBurst(origin, '#fff3c0', 20, { speed: 12, spread: 1, life: 0.35, gravity: 2, drag: 3 });
    this.spawnBurst(origin, '#6d6d75', 26, { speed: 3.4, spread: 1.5, life: 1.1, gravity: -0.6, drag: 1.6, upBias: 1 });
    this.shockwave(origin, '#ffcf6b', 4.6, 0.5);
  }

  rocketTrail(origin: THREE.Vector3): void {
    this.spawnBurst(origin, '#ffb737', 3, { speed: 0.5, life: 0.28, gravity: -0.3, drag: 2 });
    this.spawnBurst(origin, '#a6a29a', 2, { speed: 0.35, life: 0.7, gravity: -0.4, drag: 2 });
  }

  rocketExplosion(origin: THREE.Vector3): void {
    const cloud = this.fireballs[this.fireballCursor];
    this.fireballCursor = (this.fireballCursor + 1) % this.fireballs.length;
    cloud.life = 1;
    cloud.mesh.position.copy(origin);
    cloud.mesh.rotation.y = this.rng() * Math.PI * 2;
    cloud.mesh.scale.setScalar(0.3);
    cloud.mesh.material.color.set('#ffffff');
    cloud.mesh.material.opacity = 1;
    cloud.mesh.visible = true;
    this.showHitLabel(origin, this.weaponTextures.get('bazooka')!);
    this.spawnExplosion(origin);
    this.spawnBurst(origin, '#ff6a14', 70, { speed: 13, life: 0.75, gravity: 9, drag: 2 });
    this.spawnBurst(origin, '#ffda6b', 28, { speed: 18, life: 0.55, gravity: 12, drag: 1.8 });
    this.shockwave(origin, '#fff3ba', 5.5, 0.45);
  }

  spawnTrail(origin: THREE.Vector3, color: string | number): void {
    const c = new THREE.Color(color);
    this.spawn(origin, c, { speed: 0.5, life: 0.42, gravity: 0.4, drag: 3 });
  }

  spawnImpact(origin: THREE.Vector3, color: string | number = '#fff1c9', count = 10): void {
    this.spawnBurst(origin, color, count, { speed: 5.4, spread: 1.2, life: 0.34, gravity: 7, drag: 3.4 });
  }

  spawnHitLabel(origin: THREE.Vector3, style?: AttackStyle): void {
    this.showHitLabel(origin, style ? this.attackTextures.get(style)! : this.hitTexture);
  }

  private showHitLabel(origin: THREE.Vector3, texture: THREE.CanvasTexture): void {
    const label = this.hitLabels[this.hitCursor];
    this.hitCursor = (this.hitCursor + 1) % this.hitLabels.length;
    label.life = 0.6;
    label.sprite.position.set(origin.x, origin.y + 0.9, origin.z);
    label.sprite.scale.set(1.69, 0.49, 1);
    label.sprite.material.rotation = 0;
    label.sprite.material.opacity = 1;
    label.sprite.material.map = texture;

    label.sprite.visible = true;
  }

  attackSweep(origin: THREE.Vector3, direction: THREE.Vector3, style: AttackStyle): void {
    const arc = this.arcs[this.arcCursor];
    this.arcCursor = (this.arcCursor + 1) % this.arcs.length;
    arc.life = 0.4;
    arc.mesh.position.copy(origin);
    arc.style = style;
    arc.yaw = Math.atan2(direction.x, direction.z);
    arc.height = origin.y + (style === 'slam' ? 0.15 : style === 'kick' ? 0.55 : 0.85);
    this.poseSweep(arc, 0);
    arc.mesh.material.color.set(ATTACKS[style].color);
    arc.mesh.material.opacity = 0.9;
    arc.mesh.visible = true;
  }

  private poseSweep(arc: typeof this.arcs[number], progress: number): void {
    const mesh = arc.mesh;
    const sweep = 1 - (1 - progress) * (1 - progress);
    mesh.position.y = arc.height;
    if (arc.style === 'uppercut') {
      mesh.rotation.set(0, arc.yaw - Math.PI / 2, -1.4 + sweep * 2.7);
      mesh.scale.set(0.75 + sweep * 0.25, 1.15 + sweep * 0.5, 1);
      mesh.position.y += sweep * 0.7;
    } else {
      const spin = arc.style === 'spin';
      const slam = arc.style === 'slam';
      mesh.rotation.set(-Math.PI / 2, arc.yaw, (spin ? 0 : -0.9) + sweep * (spin ? 7.5 : slam ? 0.6 : 2.8));
      const size = slam ? 0.7 + sweep * 1.5 : spin ? 1.65 : 1.1 + sweep * 0.2;
      mesh.scale.set(size, size * (arc.style === 'punch' ? 0.45 : arc.style === 'kick' ? 0.8 : 1), 1);
    }
  }

  combatImpact(origin: THREE.Vector3, style: AttackStyle): void {
    const attack = ATTACKS[style];
    this.spawnHitLabel(origin, style);
    this.spawnBurst(origin, attack.color, 36, { speed: 9, life: 0.55, gravity: 12, drag: 2,
      upBias: style === 'uppercut' ? 1.5 : style === 'slam' ? 0.15 : 0.5, spread: style === 'slam' ? 1.6 : 1 });
    this.spawnImpact(origin, '#ffffff', 16);
    this.shockwave(origin, attack.color, style === 'slam' ? 4 : 2.8, 0.5);
    if (style === 'slam') this.spawnDust(origin.clone().setY(0.1), 34);
  }

  weaponImpact(origin: THREE.Vector3, kind: WeaponType, broken: boolean): void {
    const color = WEAPONS[kind].color;
    this.showHitLabel(origin, this.weaponTextures.get(kind)!);
    this.spawnImpact(origin, '#ffffff', 12);
    if (kind === 'greatsword') {
      this.spawnBurst(origin, '#ffd286', 32, { speed: 12, life: 0.45, gravity: 13, drag: 1.4 });
      this.spawnDust(origin, 24);
      this.shockwave(origin, '#dbe4ed', 3.8, 0.5);
    }
    if (kind === 'warhammer') {
      this.spawnDust(origin, 40);
      this.spawnImpact(origin, '#fff0cd', 20);
      this.shockwave(origin, '#e7bb7a', 5.2, 0.55);
    }
    this.spawnBurst(origin, color, broken ? 48 : 20, {
      speed: broken ? 11 : 6, life: broken ? 0.9 : 0.4, gravity: 15, drag: 1.4,
    });
    if (broken && kind === 'plank') this.spawnDust(origin, 14);
    if (broken) {
      for (let i = 0; i < 12; i += 1) {
        const fragment = this.fragments[this.fragmentCursor];
        this.fragmentCursor = (this.fragmentCursor + 1) % this.fragments.length;
        fragment.life = 0.9;
        fragment.mesh.visible = true;
        fragment.mesh.position.copy(origin);
        fragment.mesh.material.color.set(color);
        fragment.mesh.scale.set(kind === 'plank' ? 0.5 : 1, kind === 'plank' ? 2.8 : 1, 0.4);
        fragment.mesh.rotation.set(this.rng() * 6, this.rng() * 6, this.rng() * 6);
        fragment.velocity.set((this.rng() - 0.5) * 12, 3 + this.rng() * 6, (this.rng() - 0.5) * 12);
      }
    }
    if (kind !== 'greatsword' && kind !== 'warhammer') this.shockwave(origin, color, broken ? 3 : 1.8, 0.4);
  }

  shockwave(origin: THREE.Vector3, color: string, maxRadius: number, ttl: number): void {
    const ring = this.ringPool[this.ringCursor];
    this.ringCursor = (this.ringCursor + 1) % MAX_RINGS;
    ring.mesh.position.set(origin.x, 0.06, origin.z);
    ring.mesh.visible = true;
    ring.material.color.set(color);
    ring.material.opacity = 0.85;
    ring.life = ttl;
    ring.ttl = ttl;
    ring.maxRadius = maxRadius;
    ring.mesh.scale.setScalar(0.3);
  }

  setChargeRing(origin: THREE.Vector3 | null, ratio: number, color: string): void {
    if (!origin) {
      this.chargeRing.visible = false;
      return;
    }
    this.chargeRing.visible = true;
    this.chargeRing.position.set(origin.x, 0.05, origin.z);
    const scale = 0.8 + ratio * 0.9;
    this.chargeRing.scale.setScalar(scale);
    this.chargeMaterial.color.set(color);
    this.chargeMaterial.opacity = 0.35 + ratio * 0.5;
  }

  update(delta: number): void {
    for (const cloud of this.fireballs) {
      if (cloud.life <= 0) continue;
      cloud.life = Math.max(0, cloud.life - delta);
      cloud.mesh.visible = cloud.life > 0;
      const t = 1 - cloud.life;
      const radius = 0.3 + 2.7 * (1 - Math.exp(-t * 10));
      cloud.mesh.scale.set(radius, radius * (0.75 + t * 0.4), radius);
      cloud.mesh.position.y += delta * 0.7;
      const smoke = THREE.MathUtils.smoothstep(t, 0.18, 0.65);
      cloud.mesh.material.color.setRGB(1 - smoke * 0.7, 1 - smoke * 0.58, 1 - smoke * 0.45);
      cloud.mesh.material.opacity = Math.min(1, cloud.life * 2.2);
    }
    for (const fragment of this.fragments) {
      if (fragment.life <= 0) continue;
      fragment.life -= delta;
      fragment.mesh.visible = fragment.life > 0;
      fragment.velocity.y -= 15 * delta;
      fragment.mesh.position.addScaledVector(fragment.velocity, delta);
      if (fragment.mesh.position.y < 0.08) {
        fragment.mesh.position.y = 0.08;
        if (fragment.velocity.y < -0.7) fragment.velocity.y *= -0.32;
        else fragment.velocity.y = 0;
        const friction = Math.exp(-12 * delta);
        fragment.velocity.x *= friction;
        fragment.velocity.z *= friction;
      }
      const tumble = Math.min(1, fragment.velocity.lengthSq() / 4);
      fragment.mesh.rotation.x += delta * 11 * tumble;
      fragment.mesh.rotation.z += delta * 8 * tumble;
      // Keep the debris shape readable until its final shrink-out.
      if (fragment.life < 0.2) fragment.mesh.scale.multiplyScalar(Math.exp(-delta * 18));
    }
    for (const arc of this.arcs) {
      if (arc.life <= 0) continue;
      arc.life = Math.max(0, arc.life - delta);
      arc.mesh.visible = arc.life > 0;
      const progress = 1 - arc.life / 0.4;
      this.poseSweep(arc, progress);
      arc.mesh.material.opacity = 0.9 * (1 - progress) * (1 - progress);
    }
    for (const label of this.hitLabels) {
      if (label.life <= 0) continue;
      label.life = Math.max(0, label.life - delta);
      label.sprite.visible = label.life > 0;
      const progress = 1 - label.life / 0.6;
      const pop = progress < 0.2 ? 0.65 + progress * 2.5 : 1 + 0.15 * Math.exp(-8 * (progress - 0.2));
      label.sprite.scale.set(2.6 * pop, 0.75 * pop, 1);
      label.sprite.position.y += delta * 0.8;
      label.sprite.material.opacity = Math.min(1, label.life / 0.2);
    }
    let renderCount = 0;
    for (const i of this.activeParticles) {
      this.life[i] -= delta;
      if (this.life[i] <= 0) {
        this.activeParticles.delete(i);
        continue;
      }
      const dragFactor = Math.max(0, 1 - this.drag[i] * delta);
      this.velocities[i * 3] *= dragFactor;
      this.velocities[i * 3 + 2] *= dragFactor;
      this.velocities[i * 3 + 1] = this.velocities[i * 3 + 1] * dragFactor - this.grav[i] * delta;
      this.positions[i * 3] += this.velocities[i * 3] * delta;
      this.positions[i * 3 + 1] += this.velocities[i * 3 + 1] * delta;
      this.positions[i * 3 + 2] += this.velocities[i * 3 + 2] * delta;
      if (this.positions[i * 3 + 1] < 0.05) {
        this.positions[i * 3 + 1] = 0.05;
        this.velocities[i * 3 + 1] *= -0.25;
      }
      const fade = this.life[i] / this.ttl[i];
      this.colors[i * 3] = this.baseColors[i * 3] * fade;
      this.colors[i * 3 + 1] = this.baseColors[i * 3 + 1] * fade;
      this.colors[i * 3 + 2] = this.baseColors[i * 3 + 2] * fade;
      this.renderPositions[renderCount * 3] = this.positions[i * 3];
      this.renderPositions[renderCount * 3 + 1] = this.positions[i * 3 + 1];
      this.renderPositions[renderCount * 3 + 2] = this.positions[i * 3 + 2];
      this.renderColors[renderCount * 3] = this.colors[i * 3];
      this.renderColors[renderCount * 3 + 1] = this.colors[i * 3 + 1];
      this.renderColors[renderCount * 3 + 2] = this.colors[i * 3 + 2];
      renderCount += 1;
    }
    const geometry = this.points.geometry;
    geometry.setDrawRange(0, renderCount);
    if (renderCount > 0) {
      const positions = geometry.getAttribute('position') as THREE.BufferAttribute;
      positions.clearUpdateRanges();
      positions.addUpdateRange(0, renderCount * 3);
      positions.needsUpdate = true;
      const colors = geometry.getAttribute('color') as THREE.BufferAttribute;
      colors.clearUpdateRanges();
      colors.addUpdateRange(0, renderCount * 3);
      colors.needsUpdate = true;
    }

    for (const ring of this.ringPool) {
      if (ring.life <= 0) continue;
      ring.life -= delta;
      if (ring.life <= 0) {
        ring.mesh.visible = false;
        continue;
      }
      const k = 1 - ring.life / ring.ttl;
      // Fast impact front, then dissipate instead of a uniformly growing disc.
      ring.mesh.scale.setScalar(0.3 + k * (2 - k) * ring.maxRadius);
      ring.material.opacity = 0.85 * (1 - k) * (1 - k);
    }
  }

  dispose(): void {
    this.fireballs[0].mesh.geometry.dispose();
    for (const cloud of this.fireballs) { cloud.mesh.material.dispose(); cloud.mesh.dispose(); }
    this.fragments[0].mesh.geometry.dispose();
    for (const fragment of this.fragments) fragment.mesh.material.dispose();
    for (const texture of this.weaponTextures.values()) texture.dispose();
    for (const texture of this.attackTextures.values()) texture.dispose();
    this.arcs[0].mesh.geometry.dispose();
    for (const arc of this.arcs) arc.mesh.material.dispose();
    for (const label of this.hitLabels) label.sprite.material.dispose();
    this.hitTexture.dispose();
    this.points.geometry.dispose();
    (this.points.material as THREE.PointsMaterial).map?.dispose();
    (this.points.material as THREE.PointsMaterial).dispose();
    this.ringPool[0].mesh.geometry.dispose();
    for (const ring of this.ringPool) ring.material.dispose();
    this.chargeRing.geometry.dispose();
    this.chargeMaterial.dispose();
  }
}
